/**
 * TTN list page (2026-06-03). 2026-08-22: серверна пагінація + сортування
 * (канон goods-sales: InfiniteDocGrid + useSignControlInfinite).
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ColDef } from 'ag-grid-community';
import { tnn, type TnnListItem } from '@/api/tnn';
import ImportedBadge from '@/shared/components/ImportedBadge';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import DocListFilters, { ContractorFilter, DocListFiltersRow, useDocListFilters } from '@/shared/components/DocListFilters';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { useSignControlInfinite } from '@/shared/hooks/useSignControl';
import { agOrgCol, useOrgNames } from '@/shared/hooks/useOrgNames';

export default function TnnPage() {
  const orgNames = useOrgNames();
  const navigate = useNavigate();
  // Стандартні фільтри (еталон /employees). Backend ТТН вимагає org —
  // якщо у фільтрі не обрано, беремо першу доступну (дефолтну з шапки).
  const f = useDocListFilters();
  const { availableOrgs } = usePermissionsByOrg('sales.view');
  const effOrg = f.orgId || availableOrgs[0]?.id || '';
  // 2026-07-31 (рішення власника): пошуковий фільтр за вантажоодержувачем.
  const [contractorFilter, setContractorFilter] = useState('');

  const { datasource, total } = useInfiniteGridDatasource<TnnListItem>(
    async (limit, offset, sort) => {
      if (!effOrg) return { items: [], total: 0 };
      return tnn.list({
        organization_id: effOrg,
        contractor_id: contractorFilter || undefined,
        date_from: f.dateFrom, date_to: f.dateTo,
        sort_by: sort?.sort_by, sort_dir: sort?.sort_dir,
        limit, offset,
      });
    },
    [effOrg, contractorFilter, ...f.queryKey],
  );

  // Контроль підписання (0429): колонка «Підпис» + червоний фон непідписаних.
  const sc = useSignControlInfinite<TnnListItem>('doc.tnn', effOrg);

  const cols = useMemo<ColDef<TnnListItem>[]>(() => [
    {
      headerName: '', width: 44, sortable: false,
      cellRenderer: (p: { data?: TnnListItem }) =>
        <ImportedBadge source={p.data?.external_source} />,
    },
    { field: 'document_number', headerName: '№', width: 150 },
    {
      field: 'document_date', headerName: 'Дата', width: 170,
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleString('uk-UA') : '',
    },
    agOrgCol(orgNames),
    { field: 'carrier_name', headerName: 'Перевізник', flex: 1, minWidth: 180 },
    { field: 'vehicle_plate', headerName: 'Авто', width: 130 },
    { field: 'driver_full_name', headerName: 'Водій', flex: 1, minWidth: 180 },
    {
      field: 'state', headerName: 'Стан', width: 110,
      cellRenderer: (p: { data?: TnnListItem }) => {
        if (!p.data) return '';
        const posted = p.data.state === 'POSTED';
        const label = posted ? '✓ Проведено'
          : p.data.state === 'CANCELLED' ? '✗ Скасовано' : 'Чернетка';
        return <span style={{ color: posted ? '#15803d' : '#64748b', fontWeight: posted ? 600 : 400 }}>{label}</span>;
      },
    },
  ], []);

  return (
    <div className="card">
      <div className="toolbar" style={{ gap: '0.4rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Товарно-транспортні накладні (ТТН)<TitleTotal total={total} /></h2>
        <button className="primary" style={{ marginLeft: 'auto' }} onClick={() => navigate('/tnn/new')}>
          + Нова ТТН
        </button>
      </div>
      <DocListFiltersRow>
        {/* 2026-08-29 (рішення власника): фільтр статусу прибрано — статус видно колонкою. */}
        <DocListFilters f={f} />
        <ContractorFilter value={contractorFilter} onChange={setContractorFilter} />
      </DocListFiltersRow>
      <InfiniteDocGrid<TnnListItem>
        copyRow={(r) => navigate(`/tnn/new?copy=${r.id}`)}
        deleteRow={{ remove: (id) => tnn.remove(id) }}
        datasource={sc.wrap(datasource)}
        columnDefs={[...cols, ...sc.column]}
        storageKey="tnn-list"
        onRowDoubleClicked={(r) => navigate(`/tnn/${r.id}`)}
        getRowStyle={(p) => p.data?.deletion_mark
          ? { opacity: 0.4 } : sc.getRowStyle(p)}
      />
    </div>
  );
}
